// Producer-defined free-form briefing fields.
//
// The general briefing has a fixed set of cards (catering, producer
// info, restrictions, ...). Producers also need their own labelled
// fields — e.g. a "Flights" card with flight details underneath. Those
// live in `briefings.custom_fields` jsonb as an ordered array of
// { id, title, content }.
//
// This module owns the type + a defensive parser once, shared between
// the producer editor (CreateEditBriefing), the web portal briefing
// (BriefingView) and the crew app (mobile briefing tab), so the three
// read the exact same shape without drifting.

export interface BriefingCustomField {
  /** Stable key for React lists + reorder safety. */
  id: string;
  /** Producer-chosen label, e.g. "Flights". */
  title: string;
  /** Free-text body shown under the title. */
  content: string;
}

/**
 * Parse the loosely-typed `custom_fields` Json into a typed array.
 * Defensive (not a blind cast): the value is read on crew devices and
 * the public portal, so malformed rows degrade to empty strings rather
 * than throwing.
 */
export function parseCustomFields(json: unknown): BriefingCustomField[] {
  if (!Array.isArray(json)) return [];
  return json
    .filter((f): f is Record<string, unknown> => !!f && typeof f === "object" && !Array.isArray(f))
    .map((f) => ({
      id: typeof f.id === "string" ? f.id : "",
      title: typeof f.title === "string" ? f.title : "",
      content: typeof f.content === "string" ? f.content : "",
    }));
}

/**
 * The subset worth rendering to crew: a field with neither a title nor
 * content is an empty editor row and must not show as a blank card.
 */
export function visibleCustomFields(fields: BriefingCustomField[]): BriefingCustomField[] {
  return fields.filter((f) => f.title.trim() !== "" || f.content.trim() !== "");
}
